/**
 * PhysicalProductDrawer — criar / editar item do catálogo (físico ou digital).
 *
 * Props:
 *   isOpen, onClose
 *   productId           — produto pai (obrigatório no create)
 *   physicalProduct     — item a editar (undefined no create)
 *   onSaved(item)       — callback após sucesso
 */

import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { physicalProductsV2API } from '../../../services/api/v2/physicalProducts';
import { centsToDisplay, parseInputToCents } from '../../../utils/currencyMask';
import Drawer from './Drawer';

const EMPTY = {
  name: '',
  sku: '',
  product_type: 'physical',
  price_cents: 0,
  weight_grams: 0,
  width_cm: 0,
  height_cm: 0,
  length_cm: 0,
};

const PhysicalProductDrawer = ({ isOpen, onClose, productId, physicalProduct, onSaved }) => {
  const isEdit = !!physicalProduct?.id;
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [topError, setTopError] = useState('');

  // Hydrate quando abrir ou trocar item
  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setTopError('');
    if (physicalProduct) {
      setForm({
        name: physicalProduct.name || '',
        sku: physicalProduct.sku || '',
        product_type: physicalProduct.product_type || 'physical',
        price_cents: Number(physicalProduct.price_cents || 0),
        weight_grams: Number(physicalProduct.weight_grams || 0),
        width_cm: Number(physicalProduct.width_cm || 0),
        height_cm: Number(physicalProduct.height_cm || 0),
        length_cm: Number(physicalProduct.length_cm || 0),
      });
    } else {
      setForm(EMPTY);
    }
  }, [isOpen, physicalProduct]);

  const update = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
    setErrors((p) => ({ ...p, [field]: undefined }));
  };

  const validate = () => {
    const newErr = {};
    if (!form.name.trim()) newErr.name = 'Nome é obrigatório';
    if (form.price_cents <= 0) newErr.price_cents = 'Preço deve ser maior que zero';
    if (form.product_type === 'physical') {
      if (!form.weight_grams || form.weight_grams < 1 || form.weight_grams > 30000) {
        newErr.weight_grams = 'Entre 1g e 30000g (30kg)';
      }
      // Correios PAC/SEDEX: até 100cm; modalidades extras chegam a 200cm.
      // Subimos limite pra 200cm pra cobrir caso geral.
      if (!form.width_cm || form.width_cm < 1 || form.width_cm > 200) newErr.width_cm = 'Entre 1 e 200 cm';
      if (!form.height_cm || form.height_cm < 1 || form.height_cm > 200) newErr.height_cm = 'Entre 1 e 200 cm';
      if (!form.length_cm || form.length_cm < 1 || form.length_cm > 200) newErr.length_cm = 'Entre 1 e 200 cm';
    }
    setErrors(newErr);
    return Object.keys(newErr).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate() || submitting) return;
    try {
      setSubmitting(true);
      setTopError('');
      const payload = {
        name: form.name.trim(),
        sku: form.sku.trim() || null,
        product_type: form.product_type,
        price_cents: Number(form.price_cents),
        weight_grams: form.product_type === 'physical' ? Number(form.weight_grams) : null,
        width_cm: form.product_type === 'physical' ? Number(form.width_cm) : null,
        height_cm: form.product_type === 'physical' ? Number(form.height_cm) : null,
        length_cm: form.product_type === 'physical' ? Number(form.length_cm) : null,
      };
      let res;
      if (isEdit) {
        res = await physicalProductsV2API.update(physicalProduct.id, payload);
      } else {
        res = await physicalProductsV2API.create({ ...payload, product_id: productId });
      }
      const saved = res.data?.physical_product || res.data?.data || res.data;
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      const data = err.response?.data || {};
      const details = Array.isArray(data.details) && data.details.length
        ? data.details.map((d) => `${d.field}: ${d.message}`).join(' · ')
        : '';
      setTopError(details || data.message || err.message || 'Erro ao salvar item');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Editar item' : 'Adicionar item ao catálogo'}
      subtitle={isEdit ? form.name : 'Cadastre um produto físico ou digital'}
      icon={form.product_type === 'digital' ? 'mdi:cloud-download-outline' : 'mdi:package-variant'}
      onSubmit={handleSubmit}
      submitting={submitting}
      submitLabel={isEdit ? 'Salvar alterações' : 'Adicionar item'}
    >
      {topError && (
        <div className="drawer-alert drawer-alert--error">
          <Icon icon="mdi:alert-circle" width="16" />
          <div>{topError}</div>
        </div>
      )}

      {/* Tipo */}
      <div className="drawer-field">
        <span className="drawer-label">Tipo do item</span>
        <div className="drawer-toggle-group">
          <button
            type="button"
            className={`drawer-toggle-btn ${form.product_type === 'physical' ? 'is-active' : ''}`}
            onClick={() => update('product_type', 'physical')}
          >
            <Icon icon="mdi:cube-outline" width="20" />
            <span className="drawer-toggle-btn--label">Físico</span>
            <span className="drawer-toggle-btn--desc">Requer envio</span>
          </button>
          <button
            type="button"
            className={`drawer-toggle-btn ${form.product_type === 'digital' ? 'is-active' : ''}`}
            onClick={() => update('product_type', 'digital')}
          >
            <Icon icon="mdi:cloud-download-outline" width="20" />
            <span className="drawer-toggle-btn--label">Digital</span>
            <span className="drawer-toggle-btn--desc">Download/curso</span>
          </button>
        </div>
      </div>

      <div className="drawer-field--row">
        <div className="drawer-field">
          <span className="drawer-label drawer-label--required">Nome</span>
          <input
            className="drawer-input"
            type="text"
            value={form.name}
            onChange={(e) => update('name', e.target.value)}
            placeholder="Ex: Cápsulas frasco 60u"
            maxLength={120}
          />
          {errors.name && (
            <span className="drawer-error">
              <Icon icon="mdi:alert-circle-outline" width="12" /> {errors.name}
            </span>
          )}
        </div>
        <div className="drawer-field">
          <span className="drawer-label">SKU (opcional)</span>
          <input
            className="drawer-input"
            type="text"
            value={form.sku}
            onChange={(e) => update('sku', e.target.value)}
            placeholder="Ex: CAPS-60"
            maxLength={64}
          />
        </div>
      </div>

      <div className="drawer-field">
        <span className="drawer-label drawer-label--required">Preço unitário</span>
        <div className="drawer-input--with-suffix">
          <input
            className="drawer-input"
            type="text"
            inputMode="numeric"
            value={centsToDisplay(form.price_cents)}
            onChange={(e) => update('price_cents', parseInputToCents(e.target.value))}
            placeholder="0,00"
            style={{ paddingLeft: 36 }}
          />
          <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#6b7280', fontSize: 13, fontWeight: 600 }}>R$</span>
        </div>
        <span className="drawer-help">Preço unitário do item. Privado — só você vê.</span>
        {errors.price_cents && (
          <span className="drawer-error">
            <Icon icon="mdi:alert-circle-outline" width="12" /> {errors.price_cents}
          </span>
        )}
      </div>

      {form.product_type === 'physical' && (
        <>
          <div className="drawer-field--row">
            <div className="drawer-field">
              <span className="drawer-label drawer-label--required">Peso</span>
              <div className="drawer-input--with-suffix">
                <input
                  className="drawer-input"
                  type="text"
                  inputMode="numeric"
                  // PRD-02: máscara — usuário digita em GRAMAS (inteiro), exibe formatado em KG.
                  // Internamente o form continua com weight_grams (inteiro) — só o display muda.
                  value={
                    form.weight_grams
                      ? `${(form.weight_grams / 1000).toLocaleString('pt-BR', {
                          minimumFractionDigits: 3,
                          maximumFractionDigits: 3,
                        })}`
                      : ''
                  }
                  onChange={(e) => {
                    // Aceita só dígitos. Limpa qualquer separador que o user cole.
                    const onlyDigits = e.target.value.replace(/\D/g, '');
                    update('weight_grams', onlyDigits ? parseInt(onlyDigits, 10) : 0);
                  }}
                  placeholder="0,200"
                />
                <span className="drawer-input-suffix">kg</span>
              </div>
              <span className="drawer-help">
                Digite em gramas. Ex: <code>500</code> → 0,500 kg · <code>1500</code> → 1,500 kg
              </span>
              {errors.weight_grams && (
                <span className="drawer-error">
                  <Icon icon="mdi:alert-circle-outline" width="12" /> {errors.weight_grams}
                </span>
              )}
            </div>
            <div className="drawer-field">
              <span className="drawer-label drawer-label--required">Largura</span>
              <div className="drawer-input--with-suffix">
                <input
                  className="drawer-input"
                  type="number"
                  value={form.width_cm || ''}
                  onChange={(e) => update('width_cm', parseInt(e.target.value, 10) || 0)}
                  placeholder="5"
                />
                <span className="drawer-input-suffix">cm</span>
              </div>
              <span className="drawer-help">Mín: 1cm · Máx: 105cm</span>
              {errors.width_cm && (
                <span className="drawer-error">
                  <Icon icon="mdi:alert-circle-outline" width="12" /> {errors.width_cm}
                </span>
              )}
            </div>
          </div>

          <div className="drawer-field--row">
            <div className="drawer-field">
              <span className="drawer-label drawer-label--required">Altura</span>
              <div className="drawer-input--with-suffix">
                <input
                  className="drawer-input"
                  type="number"
                  value={form.height_cm || ''}
                  onChange={(e) => update('height_cm', parseInt(e.target.value, 10) || 0)}
                  placeholder="3"
                />
                <span className="drawer-input-suffix">cm</span>
              </div>
              <span className="drawer-help">Mín: 1cm · Máx: 105cm</span>
              {errors.height_cm && (
                <span className="drawer-error">
                  <Icon icon="mdi:alert-circle-outline" width="12" /> {errors.height_cm}
                </span>
              )}
            </div>
            <div className="drawer-field">
              <span className="drawer-label drawer-label--required">Comprimento</span>
              <div className="drawer-input--with-suffix">
                <input
                  className="drawer-input"
                  type="number"
                  value={form.length_cm || ''}
                  onChange={(e) => update('length_cm', parseInt(e.target.value, 10) || 0)}
                  placeholder="10"
                />
                <span className="drawer-input-suffix">cm</span>
              </div>
              <span className="drawer-help">Mín: 1cm · Máx: 105cm</span>
              {errors.length_cm && (
                <span className="drawer-error">
                  <Icon icon="mdi:alert-circle-outline" width="12" /> {errors.length_cm}
                </span>
              )}
            </div>
          </div>

          <div className="drawer-alert drawer-alert--info">
            <Icon icon="mdi:information-outline" width="16" />
            <div>
              Dimensões reais do item. A embalagem de envio é calculada
              automaticamente a partir da composição dos planos.
            </div>
          </div>
        </>
      )}
    </Drawer>
  );
};

export default PhysicalProductDrawer;
